import { Alert, Button, Card, Form, Input, Modal, Select, Space, Table, Tag, Typography } from 'antd'
import { useState } from 'react'
import { filterTenantOption, toTenantOptions } from '../../MyTenant/tenantSelectModel'
import { useSidebarTenantsQuery } from '../../MyTenant/tenantsQueries'
import { useQuickBooksInvoicesQuery, useResyncQuickBooksInvoicesMutation } from './quickBooksResyncQueries'
import {
  QuickBooksInvoiceRow,
  filterInvoiceRows,
  getInvoicesErrorMessage,
  getLinkStateTag,
  getNoSearchMatchesMessage,
  getQueuedMessage,
  getResyncErrorMessage,
  toInvoiceRows,
} from './quickBooksResyncModel'
import InvoicePaymentsModal from './InvoicePaymentsModal'
import RelinkInvoiceModal from './RelinkInvoiceModal'

const { Paragraph, Text } = Typography

const QuickBooksResyncSection = () => {
  const [modal, modalContextHolder] = Modal.useModal()
  const [selectedTenantId, setSelectedTenantId] = useState<number | undefined>(undefined)
  const [relinkRow, setRelinkRow] = useState<QuickBooksInvoiceRow | undefined>(undefined)
  const [paymentsRow, setPaymentsRow] = useState<QuickBooksInvoiceRow | undefined>(undefined)
  const [relinkSuccessMessage, setRelinkSuccessMessage] = useState<string | undefined>(undefined)
  const [searchText, setSearchText] = useState('')
  const [tablePage, setTablePage] = useState(1)
  const tenantsQuery = useSidebarTenantsQuery()
  const invoicesQuery = useQuickBooksInvoicesQuery(selectedTenantId)
  const mutation = useResyncQuickBooksInvoicesMutation()

  const tenantName = tenantsQuery.data?.find(tenant => tenant.id === selectedTenantId)?.name ?? ''
  const rows = toInvoiceRows(invoicesQuery.data ?? [])
  const filteredRows = filterInvoiceRows(rows, searchText)
  const resyncingInvoiceId = mutation.isLoading ? mutation.variables?.invoiceId : undefined
  const isResyncingTenant = mutation.isLoading && mutation.variables?.invoiceId === undefined

  const handleTenantChange = (tenantId: number) => {
    setSelectedTenantId(tenantId)
    setRelinkRow(undefined)
    setPaymentsRow(undefined)
    setRelinkSuccessMessage(undefined)
    setSearchText('')
    setTablePage(1)
    mutation.reset()
  }

  const handleSearchChange = (value: string) => {
    setSearchText(value)
    setTablePage(1)
  }

  const handleOpenRelink = (row: QuickBooksInvoiceRow) => {
    setRelinkSuccessMessage(undefined)
    setRelinkRow(row)
  }

  const handleRelinked = (message: string) => {
    setRelinkSuccessMessage(message)
    setRelinkRow(undefined)
  }

  const handleResyncInvoice = (row: QuickBooksInvoiceRow) => {
    if (selectedTenantId === undefined) {
      return
    }
    mutation.mutate({ tenantId: selectedTenantId, invoiceId: row.invoiceId })
  }

  const handleResyncTenant = () => {
    if (selectedTenantId === undefined) {
      return
    }
    modal.confirm({
      title: `Re-sync every QuickBooks invoice for ${tenantName}?`,
      okText: 'Re-sync all',
      cancelText: 'Cancel',
      width: 540,
      content: (
        <div>
          <Paragraph style={{ marginBottom: 8 }}>
            This queues one refresh per QuickBooks-linked invoice; each cached copy is re-pulled from
            QuickBooks asynchronously, so balances update as the queue drains.
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            Re-syncing is idempotent and safe to re-run.
          </Paragraph>
        </div>
      ),
      onOk: () => {
        mutation.mutate({ tenantId: selectedTenantId })
      },
    })
  }

  const columns = [
    { title: 'Invoice #', dataIndex: 'invoiceNumber', key: 'invoiceNumber' },
    { title: 'Customer', dataIndex: 'customerName', key: 'customerName' },
    { title: 'QBO Invoice #', dataIndex: 'qboDocNumber', key: 'qboDocNumber' },
    { title: 'Total', dataIndex: 'total', key: 'total' },
    { title: 'SynkedUP Balance', dataIndex: 'balance', key: 'balance' },
    {
      title: 'Cached QBO Balance',
      dataIndex: 'cachedQboBalance',
      key: 'cachedQboBalance',
      render: (cachedQboBalance: string, row: QuickBooksInvoiceRow) => (
        <div>
          <div>{cachedQboBalance}</div>
          {row.qboPaymentNotApplied && <Tag color="red">Payment not applied in QBO</Tag>}
        </div>
      ),
    },
    {
      title: 'Last Synced',
      dataIndex: 'lastSynced',
      key: 'lastSynced',
      render: (lastSynced: string, row: QuickBooksInvoiceRow) => {
        const stateTag = getLinkStateTag(row.linkState)
        if (stateTag !== null) {
          return (
            <div>
              <Tag color={stateTag.color}>{stateTag.label}</Tag>
              {row.lastSyncedAge !== '' && (
                <div>
                  <Text type="secondary">{row.lastSyncedAge}</Text>
                </div>
              )}
            </div>
          )
        }
        return (
          <div>
            <div>{lastSynced}</div>
            <Text type="secondary">{row.lastSyncedAge}</Text>
          </div>
        )
      },
    },
    {
      title: '',
      key: 'actions',
      render: (_: unknown, row: QuickBooksInvoiceRow) => (
        <Space>
          <Button
            size="small"
            onClick={() => handleResyncInvoice(row)}
            loading={resyncingInvoiceId === row.invoiceId}
            disabled={mutation.isLoading && resyncingInvoiceId !== row.invoiceId}
          >
            Re-sync
          </Button>
          <Button size="small" onClick={() => handleOpenRelink(row)}>
            Relink
          </Button>
          <Button size="small" onClick={() => setPaymentsRow(row)}>
            Payments
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <Card title="QuickBooks Re-sync" style={{ maxWidth: 1200 }}>
      {modalContextHolder}
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Re-pulls a tenant&apos;s QuickBooks invoices when an inbound webhook was dropped and the
          cached balance went stale. Invoices are listed stalest first; an old last-synced time on an
          invoice that still shows a balance is the row to suspect.
        </Paragraph>

        <Form layout="vertical" style={{ marginBottom: 0 }}>
          <Form.Item label="Tenant" style={{ marginBottom: 0 }}>
            <Select
              showSearch
              placeholder="Search for a tenant"
              value={selectedTenantId}
              onChange={handleTenantChange}
              options={toTenantOptions(tenantsQuery.data ?? [])}
              filterOption={filterTenantOption}
              loading={tenantsQuery.isLoading}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>

        <Space>
          <Button
            type="primary"
            onClick={handleResyncTenant}
            loading={isResyncingTenant}
            disabled={selectedTenantId === undefined || rows.length === 0}
          >
            Re-sync all invoices
          </Button>
          <Button
            onClick={() => invoicesQuery.refetch()}
            loading={invoicesQuery.isFetching}
            disabled={selectedTenantId === undefined}
          >
            Refresh list
          </Button>
        </Space>

        {mutation.isSuccess && (
          <Alert
            type="success"
            showIcon
            message="Re-sync queued"
            description={getQueuedMessage(mutation.data.invoiceCount)}
          />
        )}

        {relinkSuccessMessage !== undefined && (
          <Alert type="success" showIcon message="Invoice relinked" description={relinkSuccessMessage} />
        )}

        {mutation.isError && (
          <Alert
            type="error"
            showIcon
            message="Re-sync failed"
            description={getResyncErrorMessage(mutation.error)}
          />
        )}

        {invoicesQuery.isError && (
          <Alert
            type="error"
            showIcon
            message="Failed to load the tenant's QuickBooks invoices"
            description={getInvoicesErrorMessage(invoicesQuery.error)}
          />
        )}

        {invoicesQuery.isSuccess && rows.length === 0 && (
          <Alert type="info" showIcon message="This tenant has no QuickBooks-linked invoices" />
        )}

        {rows.length > 0 && (
          <Input
            allowClear
            placeholder="Search by invoice #, customer, or QBO invoice #"
            value={searchText}
            onChange={event => handleSearchChange(event.target.value)}
            style={{ maxWidth: 400 }}
          />
        )}

        {rows.length > 0 && filteredRows.length === 0 && (
          <Alert type="info" showIcon message={getNoSearchMatchesMessage(searchText)} />
        )}

        {filteredRows.length > 0 && (
          <Table
            columns={columns}
            dataSource={filteredRows}
            size="small"
            loading={invoicesQuery.isFetching}
            pagination={{
              pageSize: 20,
              hideOnSinglePage: true,
              current: tablePage,
              onChange: page => setTablePage(page),
            }}
          />
        )}
      </Space>

      {selectedTenantId !== undefined && relinkRow !== undefined && (
        <RelinkInvoiceModal
          tenantId={selectedTenantId}
          invoiceId={relinkRow.invoiceId}
          invoiceNumber={relinkRow.invoiceNumber}
          onClose={() => setRelinkRow(undefined)}
          onRelinked={handleRelinked}
        />
      )}

      {selectedTenantId !== undefined && paymentsRow !== undefined && (
        <InvoicePaymentsModal
          key={paymentsRow.invoiceId}
          tenantId={selectedTenantId}
          invoiceId={paymentsRow.invoiceId}
          invoiceNumber={paymentsRow.invoiceNumber}
          onClose={() => setPaymentsRow(undefined)}
        />
      )}
    </Card>
  )
}

export default QuickBooksResyncSection
